<template>
  <a-row :gutter="16" :wrap="false">
    <a-col flex="300px" v-if="auth">
      <AsideOne :tab="tab" :list="COMPONENT_LIST" />
    </a-col>
    <a-col flex="auto" class="flex flex-col gap-3">
      <component
        :is="markRaw(COMPONENT_LIST[tab].component)"
        :list="reList"
        :type="COMPONENT_LIST[tab].type"
      />
    </a-col>
  </a-row>
</template>

<script setup lang="ts">
import { getList } from "@/apis/register/get";
import type { PressReleaseList } from "@/apis/register/type";
import AsideOne from "@/components/asides/AsideOne.vue";
import type { Aside } from "@/components/asides/type";
import { allowedRoutes, type AllowedRedirectRoutes } from "@/router/constants";
import type { StepStore } from "@/stores/type";
import { IdentityStore } from "@/stores/type";
import { authenticated } from "@/utils/auth";
import { getQueryNumber } from "@/utils/helper";
import {
  computed,
  defineAsyncComponent,
  inject,
  markRaw,
  onMounted,
  ref,
  watch,
} from "vue";
import { useMeta } from "vue-meta";
import { useRoute, useRouter, type LocationQuery } from "vue-router";

const route = useRoute();
const router = useRouter();
const auth = computed(() => authenticated());

const stepStore = inject<StepStore>("stepStore");
const formTargetStore = inject<IdentityStore>("formTargetStore");

useMeta({ title: "หน้าแรก" });

const PressRelease = defineAsyncComponent(
  () => import("@/components/homeView/PressRelease.vue"),
);
const SeeDetail = defineAsyncComponent(
  () => import("@/components/homeView/SeeDetail.vue"),
);
const Manual = defineAsyncComponent(
  () => import("@/components/homeView/Manual.vue"),
);
const Status = defineAsyncComponent(
  () => import("@/components/homeView/Status.vue"),
);

const COMPONENT_LIST: Readonly<Aside[]> = [
  { id: 0, content: "หน้าแรก", component: PressRelease },
  { id: 1, content: "รายละเอียด", component: SeeDetail, hide: true },
  { id: 2, content: "คู่มือ", component: Manual, hide: true },
  { id: 3, content: "สถานะการสมัคร", component: Status, type: "A" },
  { id: 4, content: "ประวัติการสมัคร", component: Status, type: "B" },
];

const reList = ref<PressReleaseList>([]);

onMounted(async () => {
  formTargetStore?.reset();
  stepStore?.reset();
  try {
    const { data } = await getList();
    reList.value = data;
  } catch (error) {
    console.error("Failed to fetch list:", error);
  }
});

const tab = computed(() => {
  const queryTab = getQueryNumber(route.query.tab as string);
  const currentTab =
    typeof queryTab === "number" &&
    queryTab >= 0 &&
    queryTab < COMPONENT_LIST.length
      ? queryTab
      : 0;

  if (currentTab === 0) {
    window.location.href = import.meta.env.VITE_APP_LEGISLATION_URL;
  }

  return currentTab;
});

const handlePageRedirect = (query: LocationQuery) => {
  const page = query?.page as string;
  const uuid = query?.uuid as string;

  if (page && allowedRoutes.includes(page as AllowedRedirectRoutes)) {
    const path = uuid ? `/${page}/${uuid}` : `/${page}`;
    router.push({ path }).catch(console.error);
  }
};

watch(() => route.query, handlePageRedirect, { immediate: true });
</script>
